import React, { useEffect, useRef, useState } from 'react'
import { ProjectProps } from '../Project'
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../components/Loading';
import { FileModel, StorageModel } from '../../../utils/interfaces';
import { getStandardTime, useAsyncEffect, useEventListener } from '../../../utils/functions';
import { Admin, getItem, getItems } from '../../../api/API';
import { freeLimits, MAX_IMAGE_SIZE, tables } from '../../../utils/constants';
import MainBlock from '../../../components/MainBlock';
import { TbExternalLink } from 'react-icons/tb';
import Table from '../../../components/Table';
import imageCompression from 'browser-image-compression';
import StatusPopup from '../../../components/StatusPopup';
import AlertWrapper from '../../../components/AlertWrapper';
import Alert from '../../../components/Alert';
import { BoldHeader3 } from '../../../components/General';

const StorageManage = (props: ProjectProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [storage, setStorage] = useState<StorageModel | null>(null);
  const [files, setFiles] = useState<FileModel[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [fileInfos, setFileInfos] = useState<Record<string, any>[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showDeleteFilesAlert, setShowDeleteFilesAlert] = useState(false);
  const [showDownloadFilesAlert, setShowDownloadFilesAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSameNameAlert, setShowSameNameAlert] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);

  useEffect(() => {
    let filtered = [...files];
    setFileInfos(filtered.map(file => ({ [t('table-attr.general.name')]: file.name, [t('table-attr.general.size')]: file.size, [t('table-attr.file.type')]: file.type, [t('table-attr.general.created-at')]: getStandardTime(new Date(file.createdAt)) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.delete-files-button') && !target.closest('.delete-files-alert')) { // delete alert
      setShowDeleteFilesAlert(false);
    }
    if (!target.closest('.download-files-button') && !target.closest('.download-files-alert')) { // download alert
      setShowDownloadFilesAlert(false);
    }
    if (!target.closest('.same-name-alert')) { // close same name alert
      setShowSameNameAlert(false);
      setUploadingFile(null);
    }
    if (!target.closest('.reach-limit-alert')) { // close limit alert
      setShowLimitAlert(false);
    }
  }, []);
  
  // load storage and files
  useAsyncEffect(async () => {
    const st = await getItem(tables.storages, `${props.project.id}_${params.serviceName}`);
    if (!st.item) {
      navigate('/pagenotfound');
      return;
    }
    setStorage(st.item);
    const data: FileModel[] = (await getItems(tables.files, { storageId: `${props.project.id}_${params.serviceName}` })).items || [];
    if (data) {
      setFiles(data);
      setIsLoading(false);
    }
  }, [props.project, params]);

  // handle file input
  const onFileChange = async(e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(file);
    // if reach limit
    if (file.size > freeLimits.storage) {
      setShowLimitAlert(true);
    }
    // if name exists
    else if (files.find(f => f.name === file.name)) {
      setShowSameNameAlert(true);
    } // else add file
    else {
      await uploadFile(file);
    }
  }

  // upload file
  const uploadFile = async (file: File) => {
    if (!storage || !file) return;
    setIsUploading(true);
    let finalFile = file;
    // if it's decode content type
    if (file.type.startsWith('image/') && file.size > MAX_IMAGE_SIZE) {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
      };
      finalFile = await imageCompression(file, options);
    }
    
    const newFile: FileModel = { id: storage.id + '_' + finalFile.name, name: finalFile.name, type: finalFile.type, size: finalFile.size, storageId: storage.id, createdAt: new Date().toISOString() };
    await Admin.uploadLargeFile(`${props.project.id}/${storage?.name}/${newFile.name}`, newFile.type, finalFile);
    // delete original file if it's a replacement
    const originalFile = files.find(f => f.name === newFile.name);
    if (originalFile) {
      setFiles([...files.filter(f => f.name !== newFile.name), newFile]);
    } else {
      setFiles([...files, newFile]);
    }
    setIsUploading(false);
  }

  // replace existing file if same name
  const replaceFile = async () => {
    if (!uploadingFile) return;
    setShowSameNameAlert(false);
    await uploadFile(uploadingFile);
  }

  // download files
  const downloadFiles = async () => {
    if (!storage) return;
    setIsDownloading(true);
    for (const name of selected) {
      const file = files.find(f => f.name === name);
      if (file) {
        await Admin.downloadFile(`${props.project.id}/${storage.name}/${name}`);
      }
    }
    setIsDownloading(false);
    setShowDownloadFilesAlert(false);
  }

  // delete files and folders
  const deleteFiles = async () => {
    if (!storage) return;
    setIsDeleting(true);
    for (const name of selected) {
      const file = files.find(f => f.name === name);
      if (!file) continue;
      await Admin.deleteFile(`${props.project.id}/${storage.name}/${name}`);
    }
    setFiles(prev => prev.filter(f => !selected.includes(f.name)));
    setSelected([]);
    setShowDeleteFilesAlert(false);
    setIsDeleting(false);
  }

  return (
    isLoading ? <main className='flex items-center justify-center flex-1'><Loading solid size={8} /></main>
      : <>
        <div className='flex-1 flex flex-col p-4 md:px-8 overflow-auto'>
          <div className='flex items-center justify-between overflow-hidden gap-4'>
            <div className='flex items-center flex-1 overflow-hidden'>
              <button onClick={() => navigate(`/project/${props.project.id}/storage`)} className='h-8 w-8 flex items-center justify-center rounded-full mr-2'><i className='fa-solid fa-arrow-left' /></button>
              <h1 className='flex-1 md:text-2xl whitespace-nowrap overflow-hidden text-ellipsis'>{storage?.name}</h1>
            </div>
          </div>
          <div className='flex-1 overflow-auto mt-4 flex flex-col'>
            <MainBlock extraclass='flex-1 flex flex-col overflow-auto'>
              <div className='flex items-center justify-between flex-wrap'>
                <BoldHeader3>{t('project.storage.all-files')} ({fileInfos.length})</BoldHeader3>
                <div className='flex my-1 flex-wrap gap-2'>
                  <button onClick={() => setShowDeleteFilesAlert(true)} disabled={selected.length === 0} className='delete-files-button disabled:opacity-30 bg-gray-100 rounded-lg p-2 px-4 text-xs'>{t('project.storage.delete-files')}</button>
                  <button onClick={() => setShowDownloadFilesAlert(true)} disabled={selected.length === 0} className='download-files-button disabled:opacity-30 bg-gray-100 rounded-lg p-2 px-4 text-xs'>{t('project.storage.download-files')}</button>
                  <button onClick={() => setTimeout(() => fileInputRef.current?.click(), 10)} disabled={isUploading} className='upload-file-button disabled:opacity-30 bg-primary text-white rounded-lg p-2 px-4 text-xs'>{t('project.storage.upload-files')}</button>
                </div>
              </div>
              <div className='flex flex-col flex-1 overflow-auto mt-2'>
                <Table
                  data={fileInfos}
                  setData={setFileInfos}
                  selected={selected}
                  setSelected={setSelected}
                  attrKey={t('table-attr.general.name')}
                  valueTypes={{ [t('table-attr.general.size')]: 'byte' }}
                  attributes={[{ name: t('table-attr.general.name'), type: 'string' }, { name: t('table-attr.general.size'), type: 'number' }, { name: t('table-attr.file.type'), type: 'string' }, { name: t('table-attr.general.created-at'), type: 'string' }]}
                />
                {fileInfos.length === 0 && <div className='flex-1 flex items-center justify-center font-light text-sm'>{t('project.storage.no-item')}</div>}
              </div>
            </MainBlock>
          </div>
        </div>

        {/* hidden file input */}
        <input type='file' ref={fileInputRef} onChange={onFileChange} style={{ display: 'none' }} />

        {/* download files alert */}
        {showDownloadFilesAlert && <AlertWrapper>
          <Alert 
            extraclass='download-files-alert'
            title={t('project.storage.download-files')}
            maxWidth={480}
            onClose={() => setShowDownloadFilesAlert(false)}
            body={<div className='mx-1'>
              <div dangerouslySetInnerHTML={{ __html: t(selected.length === 1 ? 'project.storage.download-file-message' : 'project.storage.download-files-message', { count: selected.length }) }} />
            </div>}
            bottom={<div className='flex'>
              <button onClick={downloadFiles} disabled={selected.length === 0 || isDownloading} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isDownloading ? <Loading /> : t('general.download')}</button>
            </div>}
          />  
        </AlertWrapper>}

        {/* delete items alert */}
        {showDeleteFilesAlert && <AlertWrapper>
          <Alert 
            extraclass='delete-files-alert'
            title={t('project.storage.delete-files')}
            maxWidth={480}
            onClose={() => setShowDeleteFilesAlert(false)}
            body={<div className='mx-1'>
              <div dangerouslySetInnerHTML={{ __html: t(selected.length <= 1 ? 'project.storage.delete-file-message' : 'project.storage.delete-files-message', { count: selected.length }) }} />
            </div>}
            bottom={<div className='flex'>
              <button onClick={deleteFiles} disabled={selected.length === 0 || isDeleting} className='disabled:bg-gray-300 bg-primary text-white text-sm p-2 px-8 rounded-lg'>{isDeleting ? <Loading /> : t('general.delete')}</button>
            </div>}
          />  
        </AlertWrapper>}

        {/* same file name */}
        {(showSameNameAlert && uploadingFile) && <AlertWrapper>
          <Alert
            extraclass='same-name-alert'
            title={t('project.storage.same-file-name')}
            maxWidth={480}
            onClose={() => setShowSameNameAlert(false)}
            body={<div className='mx-1'>
              <div dangerouslySetInnerHTML={{ __html: t('project.storage.same-file-name-message') }} />
            </div>}
            bottom={<div className='flex'>
              <button onClick={replaceFile} className='flex items-center justify-center bg-primary text-white text-sm p-2 px-8 rounded-lg'>{t('general.replace')}</button>
            </div>}
          />
        </AlertWrapper>}

        {/* limit alert */}
        {showLimitAlert && <AlertWrapper>
          <Alert
            extraclass='reach-limit-alert'
            title={t('console.storage.reach-limit')}
            maxWidth={480}
            onClose={() => setShowLimitAlert(false)}
            body={<div className='mx-1'>
              <div dangerouslySetInnerHTML={{ __html: t('console.storage.reach-limit-message') }} />
            </div>}
            bottom={<div className='flex'>
              <button onClick={() => setShowLimitAlert(false)} className='flex items-center justify-center bg-primary text-white text-sm p-2 px-8 rounded-lg'>OK</button>
            </div>}
          />
        </AlertWrapper>}

        {/* uploading status */}
        {isUploading && <StatusPopup status={t('project.storage.uploading')} />}
      </>
  )
}

export default StorageManage